import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IExpenseSummary } from '../IExpenseSummary';
import { IDirectReport, ITravelType } from '../../lookups';
import { IExpenseLine } from '../IExpenseLine';
import { IExpenseReceipt } from '../IExpenseReceipt';
import { IPaymentMethod } from '../IPaymentMethod';
import { ITravelMode } from '../ITravelMode';
import { IExpenseTask } from '../IExpenseTask';
import { ITravelRate } from '../ITravelRate';
import { ITripLegType } from '../ITripLegType';
import { IAttachment } from '../../shared';
import { ExpenseRequestTypeEnum } from '../expense-enums';
import * as actions from './actions';
import * as fromShared from '../../shared';
import { UserPaymentMethod } from '../UserPaymentMethod';

export interface State {
    expenseTasks: Array<IExpenseTask>;
    expenseSummaries: Array<IExpenseSummary>;
    expenseLines: Array<IExpenseLine>;
    expenseReceipts: Array<IExpenseReceipt>;
    paymentMethods: Array<IPaymentMethod>;
    userPaymentMethods: Array<UserPaymentMethod>;
    travelModes: Array<ITravelMode>;
    selectedUser: IDirectReport | null;
    editingExpenseSummary: IExpenseSummary | null;
    editingExpenseDetail: IExpenseLine | null;
    editingExpenseReceipt: IExpenseReceipt | null;
    editingExpenseReceiptAttachment: Partial<IAttachment> | null;
    receiptImage: string | null; // base64 encoded image
    travelRates: Array<ITravelRate>;
    selectedRate: ITravelRate | null;
    tripLegTypes: Array<ITripLegType>;
    successMessages?: string | null;
    failureMessages?: string | null;
    selectedExpenses: Array<number>;
    directReports: Array<IDirectReport>;
    pdbo: any;
    postSaveDetailNavigation: actions.PostSaveDetailNavigation | null;
}

export const initialState: State = {
    expenseTasks: [],
    expenseSummaries: [],
    expenseLines: [],
    expenseReceipts: [],
    paymentMethods: [],
    userPaymentMethods: [],
    travelModes: [],
    selectedUser: null,
    editingExpenseSummary: null,
    editingExpenseDetail: null,
    editingExpenseReceipt: null,
    editingExpenseReceiptAttachment: null,
    receiptImage: null,
    travelRates: [],
    selectedRate: null,
    tripLegTypes: [],
    successMessages: null,
    failureMessages: null,
    selectedExpenses: [],
    directReports: [],
    pdbo: null,
    postSaveDetailNavigation: null
};

export function reducer(
    state = initialState,
    action: actions.ActionsUnion
): State {
    try {
        switch (action.type) {
            case actions.ActionTypes.Reset:
                return initialState;
            case actions.ActionTypes.ResetSelectedExpense:
                return {
                    ...state,
                    editingExpenseSummary: null,
                    editingExpenseDetail: null,
                    editingExpenseReceipt: null,
                    editingExpenseReceiptAttachment: null,
                    receiptImage: null,
                    expenseLines: [],
                    expenseReceipts: [],
                    successMessages: null,
                    failureMessages: null
                };
            case actions.ActionTypes.ClearMessages:
                return {
                    ...state,
                    successMessages: null,
                    failureMessages: null
                };
            case actions.ActionTypes.AddExpenseSummarySuccess:
                return {
                    ...state,
                    expenseSummaries: state.expenseSummaries
                        .filter(
                            x =>
                                !action.payload.Items.some(
                                    y => y.ExpReportNbr === x.ExpReportNbr
                                )
                        )
                        .concat(action.payload.Items), // add any new
                    editingExpenseSummary: action.payload.Items[0] // we currently only edit one at a time
                };
            case actions.ActionTypes.AddExpenseSummaryFailure:
                return {
                    ...state,
                    failureMessages: action.payload.Items.map(
                        x => x?.Status?.Message
                    ).reduce((a, c) => a + ' ' + c, ''),
                    successMessages: null
                };
            case actions.ActionTypes.AddExpenseDetailsFailure:
                return {
                    ...state,
                    failureMessages: action.payload.Items.map(
                        x => x?.Status?.Message
                    ).reduce((a, c) => a + ' ' + c, ''),
                    successMessages: null
                };
            case actions.ActionTypes.AddExpenseLineSuccess:
                return {
                    ...state,
                    expenseLines: state.expenseLines
                        .filter(
                            x =>
                                !action.payload.Items.some(
                                    y =>
                                        y.ExpenseID === x.ExpenseID &&
                                        y.SequenceId === x.SequenceId
                                )
                        )
                        .concat(action.payload.Items), // add any new
                    successMessages: action.payload.Items.map(
                        x => x?.Status?.Message
                    ).reduce((a, c) => a + ' ' + c, ''),
                    failureMessages: null
                };
            case actions.ActionTypes.AddExpenseLineFailure:
                return {
                    ...state,
                    failureMessages:
                        action.payload.ErrorMessages.reduce(
                            (a, c) => a + ' ' + c,
                            ''
                        ) +
                        action.payload.Items.map(
                            x => x?.Status?.Message
                        ).reduce((a, c) => a + ' ' + c, ''),
                    successMessages: null
                };
            case actions.ActionTypes.AddExpenseReceiptsSuccess:
                return {
                    ...state,
                    expenseReceipts: state.expenseReceipts
                        .filter(
                            x =>
                                !action.payload.Items.some(
                                    y => y.Unique_Id === x.Unique_Id
                                )
                        )
                        .concat(action.payload.Items), // add any new
                    expenseLines: state.expenseLines
                        .filter(
                            x =>
                                !action.payload.Items.some(
                                    y =>
                                        y.ExpenseID === x.ExpenseID &&
                                        y.Sequenceid === x.SequenceId
                                )
                        )
                        .concat(
                            state.expenseLines
                                .filter(x =>
                                    action.payload.Items.some(
                                        y =>
                                            y.ExpenseID === x.ExpenseID &&
                                            y.Sequenceid === x.SequenceId
                                    )
                                )
                                .map(x => ({
                                    ...x,
                                    Row_Version: action.payload.Items.find(
                                        y =>
                                            y.ExpenseID === x.ExpenseID &&
                                            y.Sequenceid === x.SequenceId
                                    )?.Line_rowversion
                                }))
                        ),
                    editingExpenseReceipt: action.payload.Items[0],
                    successMessages: action.payload.Items.map(
                        x => x?.Status?.Message
                    ).reduce((a, c) => a + ' ' + c, ''),
                    failureMessages: null
                };
            case actions.ActionTypes.AddExpenseReceiptsFailure:
                return {
                    ...state,
                    failureMessages:
                        action.payload.ErrorMessages.reduce(
                            (a, c) => a + ' ' + c,
                            ''
                        ) +
                        action.payload.Items.map(
                            x => x?.Status?.Message
                        ).reduce((a, c) => a + ' ' + c, ''),
                    successMessages: null
                };
            case actions.ActionTypes.AddExpenseAttachmentSuccess: {
                return {
                    ...state,
                    editingExpenseReceiptAttachment: action.payload.Items[0],
                    successMessages: action.payload.Items.map(
                        x => x.Status.Message
                    ).reduce((a, c) => a + ' ' + c, ''),
                    failureMessages: null
                };
            }
            case actions.ActionTypes.AddExpenseAttachmentFailure: {
                return {
                    ...state,
                    failureMessages:
                        action.payload.ErrorMessages.reduce(
                            (a, c) => a + ' ' + c,
                            ''
                        ) +
                        action.payload.Items.map(x => x.Status.Message).reduce(
                            (a, c) => a + ' ' + c,
                            ''
                        ),
                    successMessages: null
                };
            }
            case actions.ActionTypes.SetExpenseSummaries:
                return {
                    ...state,
                    expenseSummaries: [...action.payload],
                    successMessages: null,
                    failureMessages: null
                };
            case actions.ActionTypes.SelectDirectReport:
                return {
                    ...state,
                    selectedUser: { ...action.payload }
                };
            case actions.ActionTypes.EditExpenseSummary:
                return {
                    ...state,
                    editingExpenseSummary: { ...action.payload }
                };
            case actions.ActionTypes.EditExpenseDetail:
                return {
                    ...state,
                    editingExpenseDetail: { ...action.payload }
                };
            case actions.ActionTypes.SetPostSaveDetailNavigation:
                return {
                    ...state,
                    postSaveDetailNavigation: action.payload
                };
            case actions.ActionTypes.AddDraftExpenseLine: {
                const reportNbr =
                    action.payload.ExpReportNbr ??
                    state.editingExpenseSummary?.ExpReportNbr ??
                    -1;
                const maxSequenceId = state.expenseLines.reduce(
                    (max, line) => Math.max(max, line.SequenceId ?? 0),
                    0
                );
                const draft: IExpenseLine = {
                    ExpReportNbr: reportNbr,
                    SequenceId: action.payload.SequenceId ?? maxSequenceId + 1,
                    ExpenseID: 0,
                    ...action.payload
                };
                const expenseLines = state.expenseLines
                    .filter(
                        line =>
                            !(
                                (line.ExpenseID ?? 0) <= 0 &&
                                line.SequenceId === draft.SequenceId
                            )
                    )
                    .concat(draft);
                return {
                    ...state,
                    expenseLines,
                    editingExpenseDetail: draft
                };
            }
            case actions.ActionTypes.EditExpenseReceipt:
                return {
                    ...state,
                    editingExpenseReceipt: { ...action.payload }
                };
            case actions.ActionTypes.SetExpenseLines:
                return {
                    ...state,
                    expenseLines: action.payload ? [...action.payload] : [],
                    editingExpenseDetail: state.editingExpenseDetail
                        ? action.payload.find(
                              x =>
                                  x.ExpenseID ===
                                  state.editingExpenseDetail?.ExpenseID
                          ) || state.editingExpenseDetail
                        : null,
                    successMessages: null,
                    failureMessages: null
                };
            case actions.ActionTypes.SetExpenseReceipts:
                return {
                    ...state,
                    expenseReceipts: action.payload ? [...action.payload] : [],
                    editingExpenseReceipt: state.editingExpenseReceipt
                        ? action.payload.find(
                              x =>
                                  x.Unique_Id ===
                                  state.editingExpenseReceipt?.Unique_Id
                          ) || action.payload[0]
                        : action.payload[0],
                    successMessages: null,
                    failureMessages: null
                };
            case actions.ActionTypes.SetPaymentMethods:
                return {
                    ...state,
                    paymentMethods: action.payload ? [...action.payload] : []
                };
            case actions.ActionTypes.SetUserPaymentMethods:
                return {
                    ...state,
                    userPaymentMethods: action.payload ? [...action.payload] : []
                };
            case actions.ActionTypes.SetTravelMode:
                return {
                    ...state,
                    travelModes: action.payload ? [...action.payload] : []
                };
            case actions.ActionTypes.SetReceiptImage:
                return {
                    ...state,
                    receiptImage: action.payload
                };
            case actions.ActionTypes.SetExpenseTasks:
                return {
                    ...state,
                    expenseTasks: action.payload ? [...action.payload] : []
                };
            case actions.ActionTypes.UpdateExpenseSummary:
                if (action.payload && action.payload.ExpReportNbr) {
                    const nextState = {
                        ...state,
                        expenseSummaries: state.expenseSummaries
                            .filter(
                                x =>
                                    x.ExpReportNbr !==
                                    action.payload.ExpReportNbr
                            )
                            .concat(action.payload)
                    };

                    const status = action.payload.Status;
                    if (
                        status &&
                        status.Message &&
                        status.StatusCode >= fromShared.DisplayMessageMinimum &&
                        !fromShared.Errors.IsSuccessStatus(status.StatusCode)
                    ) {
                        return {
                            ...nextState,
                            failureMessages: (
                                (state.failureMessages || '') +
                                ' ' +
                                status.Message
                            ).trim(),
                            successMessages: null
                        };
                    }

                    return nextState;
                } else {
                    return state;
                }
            case actions.ActionTypes.RemoveExpenseSummary:
                return {
                    ...state,
                    expenseSummaries: state.expenseSummaries.filter(
                        x => x.ExpReportNbr !== action.payload.ExpReportNbr
                    )
                };
            case actions.ActionTypes.RemoveExpenseDetail:
                return {
                    ...state,
                    expenseSummaries:
                        state.expenseLines.filter(
                            x => x.ExpenseID !== action.payload.ExpenseID
                        ).length === 0
                            ? state.expenseSummaries.filter(
                                  x =>
                                      x.ExpReportNbr !==
                                      action.payload.ExpReportNbr
                              )
                            : state.expenseSummaries,
                    expenseLines: state.expenseLines.filter(
                        x => x.ExpenseID !== action.payload.ExpenseID
                    ),
                    expenseReceipts: state.expenseReceipts.filter(
                        x => x.ExpenseID !== action.payload.ExpenseID
                    )
                };
            case actions.ActionTypes.RemoveExpenseReceipt:
                return {
                    ...state,
                    expenseReceipts: state.expenseReceipts.filter(
                        x => x.Unique_Id !== action.payload.Items[0].EntityID
                    )
                };
            case actions.ActionTypes.SetTravelRates:
                return {
                    ...state,
                    travelRates: [...(action.payload || [])]
                };
            case actions.ActionTypes.SetTripLegTypes:
                return {
                    ...state,
                    tripLegTypes: action.payload
                };
            case actions.ActionTypes.SelectTravelRate:
                return {
                    ...state,
                    selectedRate: action.payload
                };
            case actions.ActionTypes.LoadExpenseReceiptAttachment:
                return {
                    ...state,
                    editingExpenseReceiptAttachment: null
                };
            case actions.ActionTypes.EditExpenseReceiptAttachment:
                return {
                    ...state,
                    editingExpenseReceiptAttachment: action.payload
                };
            case actions.ActionTypes.AddSelectedExpense:
                return {
                    ...state,
                    selectedExpenses: fromShared.uniqBy(
                        [...state.selectedExpenses, action.payload],
                        x => x
                    )
                };
            case actions.ActionTypes.RemoveSelectedExpense:
                return {
                    ...state,
                    selectedExpenses: state.selectedExpenses.filter(
                        x => x !== action.payload
                    )
                };
            case actions.ActionTypes.SetExpenseReports:
                return {
                    ...state,
                    directReports: action.payload
                };
            case actions.ActionTypes.ClearSelectedExpenses:
                return {
                    ...state,
                    selectedExpenses: []
                };
            case actions.ActionTypes.ClearExpenseNumber:
                return {
                    ...state,
                    expenseSummaries: state.expenseSummaries.map(x =>
                        x.ExpReportNbr === action.payload
                            ? { ...x, ExpReportNbr: -1 }
                            : x
                    ),
                    expenseLines: state.expenseLines.map(x =>
                        x.ExpReportNbr === action.payload
                            ? { ...x, ExpReportNbr: -1 }
                            : x
                    ),
                    editingExpenseSummary:
                        state.editingExpenseSummary?.ExpReportNbr ===
                        action.payload
                            ? {
                                  ...state.editingExpenseSummary,
                                  ExpReportNbr: -1
                              }
                            : state.editingExpenseSummary
                };
            case actions.ActionTypes.SetExpenseLinePDBO:
                return {
                    ...state,
                    pdbo: action.pdbo
                };
            default:
                return state;
        }
    } catch (err) {
        console.error(err);
        return state;
    }
}
const expenseState = createFeatureSelector<State>('expenses');
export const getExpenseSummaries = createSelector(expenseState, x =>
    x.expenseSummaries.filter(y => y.ERTypeID !== ExpenseRequestTypeEnum.Travel)
);
export const getTravelRequestSummaries = createSelector(expenseState, x =>
    x.expenseSummaries.filter(y => y.ERTypeID === ExpenseRequestTypeEnum.Travel)
);
export const getSelectedUser = createSelector(
    expenseState,
    x => x.selectedUser
);
export const getExpenseLines = createSelector(
    expenseState,
    x => x.expenseLines
);
export const getExpenseReceipts = createSelector(
    expenseState,
    x => x.expenseReceipts
);
export const getExpensePaymentMethods = createSelector(
    expenseState,
    x => x.paymentMethods
);
export const getUserPaymentMethods = createSelector(
    expenseState,
    x => x.userPaymentMethods
);
export const getExpenseTravelModes = createSelector(
    expenseState,
    x => x.travelModes
);
export const getReceiptImage = createSelector(
    expenseState,
    x => x.receiptImage
);
export const getExpenseTasks = createSelector(
    expenseState,
    x => x.expenseTasks
);
export const getTravelRates = createSelector(expenseState, x => x.travelRates);
export const getSelectedRate = createSelector(
    expenseState,
    x => x.selectedRate
);
export const getTripLegTypes = createSelector(
    expenseState,
    x => x.tripLegTypes
);
export const getSuccessMessages = createSelector(
    expenseState,
    x => x.successMessages
);
export const getFailureMessages = createSelector(
    expenseState,
    x => x.failureMessages
);
export const getEditingExpenseSummary = createSelector(
    expenseState,
    x => x.editingExpenseSummary
);
export const getEditingExpenseDetail = createSelector(
    expenseState,
    x => x.editingExpenseDetail
);
export const getPostSaveDetailNavigation = createSelector(
    expenseState,
    x => x.postSaveDetailNavigation
);
export const getEditingExpenseReceipt = createSelector(
    expenseState,
    x => x.editingExpenseReceipt
);
export const getReceiptAttachment = createSelector(
    expenseState,
    x => x.editingExpenseReceiptAttachment
);
export const getExpenseReports = createSelector(
    expenseState,
    x => x.directReports
);
export const getExpenseLinePdbo = createSelector(expenseState, x => x.pdbo);

export const selectSummaryById = (id: number | undefined) =>
    createSelector(getExpenseSummaries, summaries =>
        summaries.find(x => x.ExpReportNbr === id)
    );
